import React from 'react';
import { H1, H3, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Amount,
  DashboardGrid,
  FullWidthSection,
} from './styles';
import { CostMarginCard } from './CostMarginCard';
import { FinanceSummary } from './FinanceSummary';

const FINANCE_DASHBOARD_QUERY = gql`
  query FinanceDashboard {
    costProfitSnapshot {
      revenue
      shippingCost
      avgCostPerShipment
      grossMarginPercent
      receivables
      overdue
      nextPayout
      balance
    }
    payments(limit: 200, offset: 0) {
      id
      paymentId
      orderId
      amount
      amountPaid
      status
      paymentMethod
      paymentDate
    }
    invoices(limit: 200, offset: 0) {
      id
      invoiceId
      orderId
      amount
      status
      dueDate
    }
  }
`;

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const FinanceDashboard: React.FC = () => {
  const { data } = useGraphQLQuery<{
    costProfitSnapshot?: {
      revenue?: number | string;
      shippingCost?: number | string;
      avgCostPerShipment?: number | string;
      grossMarginPercent?: number | string;
      receivables?: number | string;
      overdue?: number | string;
      nextPayout?: number | string;
      balance?: number | string;
    };
    payments?: Array<{ id: number; paymentId?: string; orderId?: number; amount?: number; amountPaid?: number; status?: string; paymentMethod?: string; paymentDate?: string }>;
    invoices?: Array<{ id: number; invoiceId?: string; orderId?: number; amount?: number; status?: string; dueDate?: string }>;
  }>(FINANCE_DASHBOARD_QUERY, {});

  const costProfitSnapshot = data?.costProfitSnapshot;
  const payments = data?.payments ?? [];
  const invoices = data?.invoices ?? [];

  const paymentsPending = payments.filter((p) => (p.status || '').toLowerCase() !== 'completed' && (p.status || '').toLowerCase() !== 'paid').length;
  const invoicesOverdue = invoices.filter((i) => (i.status || '').toLowerCase() === 'overdue').length;

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>Finance & Payments</H1>
          <Subtitle>Revenue, margin, receivables and every invoice and payment moving through the ledger.</Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      <StatsGrid>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Payments Pending</StatTitle>
              <StatValue>{paymentsPending}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="CreditCard" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Invoices Overdue</StatTitle>
              <StatValue>{invoicesOverdue}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="AlertCircle" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <DashboardGrid>
        <CostMarginCard
          revenue={costProfitSnapshot ? `$${Number(costProfitSnapshot.revenue).toLocaleString()}` : '—'}
          shippingCost={costProfitSnapshot ? `$${Number(costProfitSnapshot.shippingCost).toLocaleString()}` : '—'}
          margin={costProfitSnapshot ? `${Number(costProfitSnapshot.grossMarginPercent).toFixed(2)}%` : '—'}
          avgCostPerShipment={costProfitSnapshot ? `$${Number(costProfitSnapshot.avgCostPerShipment).toLocaleString()}` : '—'}
        />
        <FinanceSummary
          receivables={costProfitSnapshot ? `$${Number(costProfitSnapshot.receivables).toLocaleString()}` : '—'}
          overdue={costProfitSnapshot ? `$${Number(costProfitSnapshot.overdue).toLocaleString()}` : '—'}
          nextPayout={costProfitSnapshot?.nextPayout ? String(costProfitSnapshot.nextPayout) : '—'}
          balance={costProfitSnapshot ? `$${Number(costProfitSnapshot.balance).toLocaleString()}` : '—'}
        />

        <FullWidthSection>
          <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <OrdersHeader>
              <H3 style={{ margin: 0 }}>Recent Payments</H3>
            </OrdersHeader>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeader>Payment ID</TableHeader>
                    <TableHeader>Order</TableHeader>
                    <TableHeader>Method</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Amount</TableHeader>
                  </tr>
                </TableHead>
                <TableBody>
                  {payments.slice(0, 20).map((p) => (
                    <TableRow key={p.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                      <TableCell>{p.paymentId ?? p.id}</TableCell>
                      <TableCell>{p.orderId ?? '—'}</TableCell>
                      <TableCell>{p.paymentMethod ?? '—'}</TableCell>
                      <TableCell>{p.status ?? '—'}</TableCell>
                      <TableCell><Amount>{formatAmount(p.amountPaid ?? p.amount)}</Amount></TableCell>
                    </TableRow>
                  ))}
                  {payments.length === 0 && (
                    <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <TableCell colSpan={5}><span style={{ color: colors.gray500 }}>No payment records</span></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </OrdersCard>
        </FullWidthSection>
      </DashboardGrid>
    </DashboardContainer>
  );
};
